import { Loading } from "@vellum/ui";
import {
  ReportScope,
  type DashboardSubscriptionSchema,
  type InternalCompanySchema,
  type InternalFacilitySchema,
  type ReportSchema,
} from "@vellum/vellum-api";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { vellumApiSdk } from "@vellum/micro-app-auth";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CompanyContactsSection } from "@/components/CompanyContactsSection";
import { CompanySelect } from "@/components/CompanySelect";
import { FacilitySelect } from "@/components/FacilitySelect";
import { MetabaseReport } from "@/components/MetabaseReport";
import { SendHistorySection } from "@/components/subscriptions/SendHistorySection";
import { SubscriptionsSection } from "@/components/subscriptions/SubscriptionsSection";
import { previousWeek, yesterday } from "@/lib/dates";
import {
  SLUG_COMPANY,
  SLUG_FACILITY,
  SLUG_FROM_DATE,
  SLUG_TO_DATE,
} from "@/lib/metabase";

/**
 * The date window a report's cadence implies, mirroring
 * `compute_report_window` in luna-lib so the embed and the emailed CSV agree.
 * A report with no cadence is not date-filtered at all.
 */
function reportWindow(
  cadence: ReportSchema["cadence"],
): { from: string; to: string } | null {
  if (cadence === "Daily") {
    const day = yesterday();
    return { from: day, to: day };
  }
  if (cadence === "Weekly") {
    return previousWeek();
  }
  return null;
}

/**
 * Renders any report from its stored definition. A company-scoped report asks
 * for a company (then a facility, when facility-scoped) before embedding; an
 * unscoped report embeds straight away. Either way the embed is filtered to
 * the chosen scope and the cadence's date window.
 */
export function ReportPage() {
  const { slug } = useParams<{ slug: string }>();

  const [report, setReport] = useState<ReportSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [company, setCompany] = useState<InternalCompanySchema | null>(null);
  const [facility, setFacility] = useState<InternalFacilitySchema | null>(null);
  const [subscription, setSubscription] =
    useState<DashboardSubscriptionSchema | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  useEffect(() => {
    if (!slug) {
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);
    // A report swap invalidates the scope picked for the previous one.
    setCompany(null);
    setFacility(null);
    setSubscription(null);

    vellumApiSdk
      .getReport({ path: { slug } })
      .then((res) => {
        if (!active) {
          return;
        }
        if (res.data) {
          setReport(res.data);
        } else {
          setError("We could not find that report.");
        }
      })
      .catch(() => {
        if (active) {
          setError("We could not load that report.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error || !report) {
    return (
      <p className="p-4 text-sm text-error">
        {error ?? "We could not find that report."}
      </p>
    );
  }

  const needsCompany = report.scope !== ReportScope.NONE;
  const needsFacility = report.scope === ReportScope.COMPANY_FACILITY;

  if (needsCompany && !company) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <CompanySelect onSelect={setCompany} />
      </div>
    );
  }

  // Facility scope implies company scope, so `company` is always set here.
  if (needsFacility && company && !facility) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Breadcrumb
          items={[
            { label: company.name, onClick: () => setCompany(null) },
            { label: report.title },
          ]}
        />
        <FacilitySelect companyId={company.id} onSelect={setFacility} />
      </div>
    );
  }

  // A Daily subscription is keyed to one facility, so a Daily report only has
  // subscriptions once the viewer has picked one.
  const canSubscribe = report.cadence !== "Daily" || facility !== null;

  const window = reportWindow(report.cadence);
  const sqlParameters: Record<string, string> = {
    ...(company ? { [SLUG_COMPANY]: company.name } : {}),
    ...(facility ? { [SLUG_FACILITY]: facility.name } : {}),
    ...(window
      ? { [SLUG_FROM_DATE]: window.from, [SLUG_TO_DATE]: window.to }
      : {}),
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Breadcrumb
        items={[
          ...(company
            ? [
                {
                  label: company.name,
                  onClick: () => {
                    setFacility(null);
                    setCompany(null);
                  },
                },
              ]
            : []),
          ...(facility
            ? [{ label: facility.name, onClick: () => setFacility(null) }]
            : []),
          { label: report.title },
        ]}
      />
      {/* Subscriptions are per company/facility, so an unscoped report has
          none — and the subscriptions API rejects a Daily lookup without a
          facility, so a Daily report that never asks for one has none either.
          The API refuses to store that pair, but reports created before that
          rule can still reach here. */}
      {canSubscribe && company && report.cadence && (
        <SubscriptionsSection
          typ={report.cadence}
          company={company}
          facility={facility}
          metabaseReportId={report.metabase_question_id}
          onSubscriptionChange={setSubscription}
          onSent={() => setHistoryRefreshKey((key) => key + 1)}
        />
      )}
      {/* Weekly reports are the ones people circulate, so they get a jump to
          the company's contacts. Daily reports are facility-scoped and read by
          the people already on them, so the link would just be noise. */}
      {report.cadence === "Weekly" && company && (
        <CompanyContactsSection company={company} />
      )}
      {/* The embed takes whatever height is left under the header and the
          sections above it. The floor keeps it at the height it had before it
          grew to fill the page, for the case where the subscription and
          history sections leave no free space to hand it. */}
      <div className="flex min-h-[75vh] flex-1 flex-col bg-white">
        <MetabaseReport
          metabaseReportId={report.metabase_question_id}
          sqlParameters={sqlParameters}
          hiddenParameters={[SLUG_COMPANY, SLUG_FACILITY]}
        />
      </div>
      {subscription && (
        <SendHistorySection
          subscriptionId={subscription.id}
          refreshKey={historyRefreshKey}
        />
      )}
    </div>
  );
}
